import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { WorkInSumTitles, WorkOrderChapterMap } from 'constantsProject';
import type {
    ChapterEnum,
    ContractStatusEnum,
    WorkOrderVersionActKc2VersionData,
    WorkOrderPriceItem,
    WorkOrderVersionSeasonData,
    WorkOrderObjectData,
    WorkOrderPriceStatusEnum,
    FilterValueOfWorkOrderPriceItemCodeFilterData,
    WorkInSumEnum,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import {
    groupNameHeaderName,
    useTypeWorkColumns,
} from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice/hooks/useTypeWorkColumns';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { getWorkOrderUnitCellValue } from '../../helpersUerProject';
import { WorkOrderPriceCodeCell } from '../../WorkOrder/WorkOrderPrice/WorkOrderPriceCodeCell';
import { StatusCellRenderer } from '../../WorkOrder/WorkOrderTable/cellRenderers';
import { useWorkOrderSeasonColumns } from '../../WorkOrder/WorkOrderTable/hooks/useWorkOrderSeasonColumns';
import {
    uerWorkOrderVersionPriceItemChapterFilter,
    uerWorkOrderVersionPriceItemCodeFilter,
    uerWorkOrderVersionPriceItemNameFilter,
    uerWorkOrderVersionPriceItemStatusFilter,
    uerWorkOrderVersionPriceItemUnitFilter,
    uerWorkOrderVersionPriceItemWorkInSumFilter,
    uerWorkOrderVersionPriceItemWorkNameFilter,
} from '../workOrderVersionQueries';
import { WorkOrderVersionMenuCellRenderer } from '../WorkOrderVersionTable/WorkOrderVersionMenuCellRenderer';

import { useWorkOrderVersionActsColumns } from './useWorkOrderVersionActsColumns';

type TColumnsProps = {
    workOrderStatus: WorkOrderStatusEnum | undefined;
    workOrderHasInProgress: boolean;
    costRounding: number;
    refreshPriceListData: () => void;
    seasons: WorkOrderVersionSeasonData[];
    contractStatus: ContractStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    workOrderActs: WorkOrderVersionActKc2VersionData[] | undefined | null;
    changeFilterHandler: ({ fieldName, checkedValues }: any) => void;
    hasWorkKind: boolean | null | undefined;
    workOrderObjects?: WorkOrderObjectData[] | null;
    versionId: number;
    constructionTypeId: number;
    objectId?: number;
    subObjectId?: number;
};

const getHeaderTitleTooltipCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость всего, руб. без НДС',
        childTooltip: 'Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

const getHeaderTitleTooltipUnitCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
        childTooltip: 'Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

const getCodeCellClass = ({ data }: { data: WorkOrderPriceItem | undefined }) => {
    if (data?.type === WorkOrderPriceTypeEnum.Uer || data?.type === WorkOrderPriceTypeEnum.Other) {
        return 'textLeft';
    }

    return 'centeredAgGridCell';
};

export const useWorkOrderVersionColumns = ({
    workOrderStatus,
    workOrderHasInProgress,
    costRounding,
    refreshPriceListData,
    seasons,
    workOrderActs,
    contractStatus,
    isDisabledByProjectStatus,
    changeFilterHandler,
    hasWorkKind,
    workOrderObjects,
    versionId,
    constructionTypeId,
    objectId,
    subObjectId,
}: TColumnsProps) => {
    const [selection, , , setSelection] = useSelection(`workOrderVersionTable_${versionId}`);
    const [pinnedItems, , , setPinned] = usePin(`workOrderVersionTable_${versionId}`);

    const actColumns = useWorkOrderVersionActsColumns({
        workOrderActs,
        workOrderStatus,
        costRounding,
        contractStatus,
        isDisabledByProjectStatus,
    });

    const seasonsAndLinkedActsColumns = useWorkOrderSeasonColumns({
        workOrderStatus,
        actsColumns: actColumns,
        contractStatus,
        isDisabledByProjectStatus,
        hasInProgress: workOrderHasInProgress,
        seasons,
        costRounding,
    });

    const typeWorkColumns = useTypeWorkColumns({
        workOrderStatus,
        workOrderHasInProgress,
        onWorkRatioEditSuccess: refreshPriceListData,
        hasEmptyWorkRatios: false,
    });

    const filterVariables = useMemo(
        () => ({
            workOrderVersionId: versionId,
            constructionTypeId,
            rootObjectId: objectId,
            objectId: subObjectId,
        }),
        [constructionTypeId, objectId, subObjectId, versionId],
    );

    const columns = useMemo<(ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 60,
                minWidth: 60,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: WorkOrderVersionMenuCellRenderer,
                cellRendererParams: {
                    seasons,
                    workOrderObjects,
                    versionId,
                },
            },
            {
                headerName: 'Объект',
                field: 'rootObjectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Подобъект',
                field: 'objectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Раздел',
                field: 'chapter',
                flex: 1,
                minWidth: 150,
                valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                    value ? WorkOrderChapterMap[value] : '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderVersionPriceItemChapterFilter',
                    query: uerWorkOrderVersionPriceItemChapterFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                                value ? WorkOrderChapterMap[value] : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Статус',
                field: 'status',
                width: 110,
                cellRenderer: StatusCellRenderer,
                tooltipComponent: null,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderVersionPriceItemStatusFilter',
                    query: uerWorkOrderVersionPriceItemStatusFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                value ? badgePropsByStatus[value].label : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                cellClass: getCodeCellClass,
                cellRenderer: ({ data }: { data: WorkOrderPriceItem }) =>
                    !data ? '' : <WorkOrderPriceCodeCell row={data} workOrderStatus={workOrderStatus} />,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderVersionPriceItemCodeFilter',
                    query: uerWorkOrderVersionPriceItemCodeFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        { headerName: '', lockVisible: true, checkboxSelection: true, width: 50 },
                        {
                            headerName: 'Расценка',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceItemCodeFilterData }) =>
                                data?.value?.isUer ? data?.value?.uerCode : '',
                        },
                        {
                            headerName: 'МТР/Коэф.',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceItemCodeFilterData }) =>
                                !data?.value?.isUer ? data?.value?.code : '',
                        },
                    ],
                    changeFilterHandler,
                    customWhereVariablesGetter: (searchString: string) => ({
                        or: [
                            { value: { code: { containsi: searchString } } },
                            { value: { uerCode: { containsi: searchString } } },
                        ],
                    }),
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                width: 400,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderVersionPriceItemNameFilter',
                    query: uerWorkOrderVersionPriceItemNameFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: 'unit',
                width: 114,
                cellClass: 'centeredAgGridCell',
                valueGetter: getWorkOrderUnitCellValue,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderVersionPriceItemUnitFilter',
                    query: uerWorkOrderVersionPriceItemUnitFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Ед. изм.',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                headerComponent: getHeaderTitleTooltipUnitCost,
                field: 'unitCost',
                width: 180,
                wrapHeaderText: true,
                cellClass: 'ag-right-aligned-cell',
                valueFormatter: getNumberFormatter(2),
            },
            {
                headerName: 'План по НЗ',
                headerClass: 'flexStart',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: 125,
                        cellClass: 'ag-right-aligned-cell boldCell',
                        valueFormatter: getNumberFormatter(3, 6),
                    },
                    {
                        headerComponent: getHeaderTitleTooltipCost,
                        valueFormatter: getNumberFormatter(costRounding),
                        field: 'totalPlanCost',
                        width: 160,
                        cellClass: 'ag-right-aligned-cell boldCell',
                    },
                ],
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Выполнено по нз',
                          headerClass: 'flexStart',
                          marryChildren: true,
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalCompletedVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                                  valueFormatter: getNumberFormatter(3, 6),
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalCompletedCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                      {
                          headerName: 'Остаток по нз',
                          marryChildren: true,
                          headerClass: 'flexStart',
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalRemainderVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                                  valueFormatter: getNumberFormatter(3, 6),
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalRemainderCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                  ]
                : []),
            ...(typeWorkColumns && hasWorkKind
                ? (typeWorkColumns as any[]).map((typeCol: ColGroupDef) => ({
                      ...typeCol,
                      children: typeCol.children?.map((child: ColDef) => {
                          if (child?.field === 'workName') {
                              return {
                                  ...child,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderVersionPriceItemWorkNameFilter',
                                      query: uerWorkOrderVersionPriceItemWorkNameFilter,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }
                          if (child?.field === 'workInSum') {
                              return {
                                  ...child,
                                  headerClass: (child.headerClass as string)?.replace('noWrap', ''),
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      withSearch: false,
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderVersionPriceItemWorkInSumFilter',
                                      query: uerWorkOrderVersionPriceItemWorkInSumFilter,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Участие в суммировании ФО по виду работ',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                              valueFormatter: ({ value }: { value: WorkInSumEnum }) =>
                                                  WorkInSumTitles[value],
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }

                          return child;
                      }),
                  }))
                : []),
            ...seasonsAndLinkedActsColumns,
        ],
        [
            changeFilterHandler,
            costRounding,
            filterVariables,
            hasWorkKind,
            seasons,
            seasonsAndLinkedActsColumns,
            typeWorkColumns,
            versionId,
            workOrderObjects,
            workOrderStatus,
        ],
    );

    const isColumnVisible = useCallback(
        (id: string) => {
            if (id === groupNameHeaderName) {
                return selection[id] ?? false;
            }

            return selection[id] ?? true;
        },
        [selection],
    );
    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 3 || (idx > 4 && idx < 9)) {
                        newColumn.disabled = true;
                    }

                    acc.push(newColumn);

                    return acc;
                }

                return acc;
            }, []),
        [columns],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );
    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
